import { useEffect, useState } from 'react';

import { Checkbox, IconButton, Label, Select, TextField } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import ProductWallResults from 'components/NewProductWall/ProductWallResults';
import { SearchTermComboBox } from 'components/NewProductWall/SearchTermComboBox';
import { QUERY_KEYS } from 'constants/index';
import { useAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { MultiValue } from 'react-select';
import * as R from 'ramda';
import {
  productWallErrorMessageAtom,
  productWallHasErrorAtom,
} from '../../atoms/productwall';
import searchOptionsVersionClient from '../../clientAPI/searchOptionsVersion';
import { searchOptionsVerToSelectOpts } from '../../components/ProductWall/SearchOptions';
import parseArrayFromUrlString from '../../utils/parseArrayFromUrlString';
import { Channel } from 'components/NewProductWall/Channel';
import ConceptSelect from 'components/NewProductWall/ConceptSelect';
import { fetchConceptsByIds } from '../../utils/conceptHelpers';

const channelOptions = [
  { value: '82a74ac1-c527-4470-b7b0-fb5f3ef3c2e2', label: 'Nike App' },
  { value: 'd9a5bc42-4b9c-4976-858a-f159cf99c647', label: 'Nike.COM' },
  { value: '15d92135-394e-487a-8afa-f6c8525c9ea9', label: 'WeChat' },
  { value: '008be467-6c78-4079-94f0-70e2d6cc4003', label: 'SNKRS' },
];

const languageOptions = [
  { value: 'zh-Hans', label: 'ZH-HANS' },
  { value: 'en', label: 'EN' },
];

const marketplaceOptions = [
  { value: 'CN', label: 'Greater China' },
  { value: 'US', label: 'United States' },
];

const suggestionFlowOptions = [
  { value: 'new', label: 'NEW' },
  { value: 'new_extend', label: 'NEW_EXTEND' },
  { value: 'old', label: 'OLD' },
];

/** Matches `channelOptions` Nike App row (first entry today). */
const NIKE_APP_CHANNEL_ID = channelOptions[0].value;

function suggestionFlowForChannel(channelId: string): string {
  return channelId === NIKE_APP_CHANNEL_ID ? 'new_extend' : 'old';
}

const defaultSearchParams = {
  searchTerms: '',
  channel: channelOptions[0].value,
  language: languageOptions[0].value,
  marketplace: marketplaceOptions[0].value,
  callerId: 'search360UI',
  userAgent: 'search360UI',
  suggestion: suggestionFlowForChannel(channelOptions[0].value),
  explain: 'false',
};

function useRouterAwareUseState(searchParamName: string, initState: any) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [val, setVal] = useState(initState);
  const updater = (newVal: any) => {
    setVal(newVal);
    searchParams.set(searchParamName, newVal);
    setSearchParams(searchParams);
  };
  return [val, updater];
}

function resolveNewProductWallVersionRow(
  data: SearchOptionsVersion[] | undefined,
  consumerChannelId: string,
  channelIdUsedForVersion: string,
  searchVersion: string,
): SearchOptionsVersion | undefined {
  if (!data?.length) return undefined;
  const channelChanged = channelIdUsedForVersion !== consumerChannelId;
  const matchFromUrl =
    channelChanged || !searchVersion
      ? undefined
      : data.find((v) => v.searchVersionNumber === searchVersion);
  const channelMatch = data.find(
    (v) =>
      v.defaultForChannels?.includes(consumerChannelId) ||
      v.defaultForChannels?.includes('*'),
  );
  const defaultSearchVersions = data.filter((v) => v.default);
  const defaultSearchVersion =
    defaultSearchVersions.length > 0 ? defaultSearchVersions[0] : data[0];
  return matchFromUrl || channelMatch || defaultSearchVersion;
}

export default function ProductWall(props: any) {
  const { status, data, error } = useQuery({
    queryKey: [QUERY_KEYS.SEARCH_OPTIONS_VERSION, 'newProductWall'],
    queryFn: async () => searchOptionsVersionClient.getNewProductWallVersions(),
    onError: (err: keyable) => {
      setErrorMsg(err.message || JSON.stringify(err));
      setHasError(true);
    },
    staleTime: Infinity,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  // debugger;
  const searchOptionsView = searchParams.get('searchOptionsView') === '1';

  const [showSearchOptions, setShowSearchOptions] = useState(searchOptionsView);
  const [searchVersion, setSearchVersion] = useState(
    searchParams.get('searchVer') || '',
  );

  const defaultSearchText =
    searchParams.get('searchTerms') || defaultSearchParams.searchTerms;

  const [searchTerms, setSearchTerms] = useState(
    searchParams.get('searchTerms') || defaultSearchParams.searchTerms,
  );
  const initialChannelId =
    searchParams.get('channel') || defaultSearchParams.channel;
  const [consumerChannelId, setConsumerChannelId] = useRouterAwareUseState(
    'channel',
    initialChannelId,
  );
  /** Channel id we last resolved search version against (ref-free “previous channel”). */
  const [channelIdUsedForVersion, setChannelIdUsedForVersion] =
    useState(initialChannelId);
  const [language, setLanguage] = useRouterAwareUseState(
    'language',
    searchParams.get('language') || defaultSearchParams.language,
  );
  const [marketplace, setMarketplace] = useRouterAwareUseState(
    'marketplace',
    searchParams.get('marketplace') || defaultSearchParams.marketplace,
  );
  const [callerId, setCallerId] = useRouterAwareUseState(
    'callerId',
    searchParams.get('callerId') || defaultSearchParams.callerId,
  );
  const [userAgent, setUserAgent] = useRouterAwareUseState(
    'userAgent',
    searchParams.get('userAgent') || defaultSearchParams.userAgent,
  );
  const [suggestionFlow, setSuggestionFlow] = useRouterAwareUseState(
    'suggestion',
    searchParams.get('suggestion') ||
      suggestionFlowForChannel(initialChannelId),
  );

  const initTaxonomyIds = parseArrayFromUrlString(
    searchParams.get('taxonomyIds') || '',
  );
  const [taxonomyIds, setTaxonomyIds] =
    useState<Array<string>>(initTaxonomyIds);

  // Store full concept objects to access navigationId
  const [selectedConcepts, setSelectedConcepts] = useState<
    Array<RecommendConcept>
  >([]);

  const [showExplainPanel, setShowExplainPanel] = useRouterAwareUseState(
    'explain',
    searchParams.get('explain') || defaultSearchParams.explain,
  );

  const [hasError, setHasError] = useAtom(productWallHasErrorAtom);
  const [errorMsg, setErrorMsg] = useAtom(productWallErrorMessageAtom);

  const handleSearchTermsValueChange = useDebouncedCallback((v: string) => {
    setSearchTerms(v);
    // reset taxonomyIds and selectedConcepts on searchTerms change
    setTaxonomyIds([]);
    setSelectedConcepts([]);
    searchParams.set('searchTerms', v);
    searchParams.delete('taxonomyIds');
    setSearchParams(searchParams);
  }, 1000);

  const handleAdvanceOptionsBtnClick = () => {
    if (showSearchOptions) {
      searchParams.delete('searchOptionsView');
    } else {
      searchParams.delete('searchVer');
      searchParams.set('searchOptionsView', '1');
    }
    setSearchParams(searchParams);

    setShowSearchOptions(!showSearchOptions);
  };

  // Same rules as the effect, but computed during render so ProductWallResults
  // does not fire a request with the previous channel’s version before state catches up.
  const resolvedVersionRow =
    status === 'success' && data?.length
      ? resolveNewProductWallVersionRow(
          data,
          consumerChannelId,
          channelIdUsedForVersion,
          searchVersion,
        )
      : undefined;

  // Product wall always receives only `version` in options — never the expanded row from JSON.
  const versionIdForQuery =
    resolvedVersionRow?.searchVersionNumber || searchVersion || '';
  const searchOptionsForQuery = versionIdForQuery
    ? { version: versionIdForQuery }
    : {};

  useEffect(() => {
    if (status === 'success' && data?.length) {
      const opt = resolveNewProductWallVersionRow(
        data,
        consumerChannelId,
        channelIdUsedForVersion,
        searchVersion,
      );
      const channelChanged = channelIdUsedForVersion !== consumerChannelId;

      if (channelChanged) {
        searchParams.delete('searchVer');
        setSearchParams(searchParams);
        setSuggestionFlow(suggestionFlowForChannel(consumerChannelId));
      }
      setChannelIdUsedForVersion(consumerChannelId);

      if (opt) {
        setSearchVersion(opt.searchVersionNumber);
      } else {
        setErrorMsg('No search options verion data available!');
        setHasError(true);
      }
    }
  }, [status, data, consumerChannelId, channelIdUsedForVersion, searchVersion]);

  // Load full concept objects when taxonomyIds are available (e.g., from URL on page load)
  useEffect(() => {
    if (taxonomyIds.length > 0 && selectedConcepts.length === 0) {
      fetchConceptsByIds(taxonomyIds, (id, err) => {
        setErrorMsg(`Failed to load concept: ${id}`);
        setHasError(true);
      }).then((validConcepts) => {
        setSelectedConcepts(validConcepts);
      });
    }
  }, [taxonomyIds, selectedConcepts.length, setErrorMsg, setHasError]);

  const handleConceptSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConceptIds = R.uniq(
      newSelectOpts
        .filter((opt) => opt.value && opt.value.id)
        .map((opt) => opt.value.id),
    );

    // Store full concept objects for navigationId access
    const concepts = newSelectOpts
      .filter((opt) => opt.value && opt.value.id)
      .map((opt) => opt.value as RecommendConcept);

    setSelectedConcepts(concepts);

    setTaxonomyIds(selectedConceptIds);
    if (selectedConceptIds.length === 0) {
      searchParams.set('taxonomyIds', '');
    } else {
      const urlVal = encodeURIComponent(JSON.stringify(selectedConceptIds));
      searchParams.set('taxonomyIds', urlVal);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className='tw-w-11/12 tw-mx-auto tw-py-10'>
      <div className='tw-mb-4'>
        <Label font='title-6' htmlFor='concept-select'>
          Concepts
        </Label>
        <ConceptSelect
          conceptIds={taxonomyIds}
          onChange={handleConceptSelectChange}
          selectConceptsRef={null}
          validationError={''}
          searchableOnly={false}
        />
      </div>
      <div
        className='tw-grid tw-items-end tw-gap-1'
        style={{
          gridTemplateColumns: 'repeat(auto-fit, 240px)',
          justifyItems: 'stretch',
        }}
      >
        <div style={{ gridArea: '1 / 1 / 2 / 3' }}>
          <SearchTermComboBox
            onValueChange={handleSearchTermsValueChange}
            language={language}
            marketplace={marketplace}
            suggestionFlow={suggestionFlow}
            defaultSearchText={defaultSearchText}
          />
        </div>
        <Channel
          consumerChannelId={consumerChannelId}
          setConsumerChannelId={setConsumerChannelId}
        />
        <Select
          id='suggestionFlow-select'
          className='tw-w-60 tw-rounded-3xl'
          label='Suggestion Flow'
          value={
            suggestionFlowOptions.filter((v) => v.value === suggestionFlow)[0]
          }
          options={suggestionFlowOptions}
          onChange={(event) => {
            setSuggestionFlow(event?.value || '');
          }}
        />
        {status === 'success' && data && (
          <Select
            id='search-ver-select'
            className='tw-w-60 tw-rounded-3xl'
            label='Search Versions'
            options={searchOptionsVerToSelectOpts(data)}
            value={searchOptionsVerToSelectOpts(data).find(
              (v) => v.value === searchVersion,
            )}
            onChange={(event) => {
              const verKey = event?.value || '';
              setSearchVersion(verKey);
              searchParams.set('searchVer', verKey);
              setSearchParams(searchParams);
            }}
          />
        )}
        <div className='tw-mx-3'>
          <Checkbox
            className='tw-mr-3'
            id='toggle-explain'
            name='explain'
            label='Explain'
            checked={showExplainPanel === 'true'}
            onChange={(v) => setShowExplainPanel(v.target.checked + '')}
          />
          <IconButton
            label=''
            variant='ghost'
            icon={showSearchOptions ? 'Collapse' : 'Expand'}
            onClick={handleAdvanceOptionsBtnClick}
          />
        </div>
      </div>
      {showSearchOptions && (
        <div
          className='tw-grid tw-items-center tw-gap-1 tw-mt-3'
          style={{
            gridTemplateColumns: 'repeat(auto-fit, 240px)',
            justifyItems: 'stretch',
          }}
        >
          <TextField
            id='callerId'
            type='text'
            label='Caller ID'
            placeholder='Please input Caller ID'
            value={callerId}
            onChange={(event) => {
              setCallerId(event.target.value);
            }}
          />
          <TextField
            id='userAgent'
            type='text'
            label='User Agent'
            placeholder='Please input User Agent'
            value={userAgent}
            onChange={(event) => {
              setUserAgent(event.target.value);
            }}
          />
        </div>
      )}
      <br />
      {status === 'success' && (
        <ProductWallResults
          searchTerms={searchTerms}
          marketplace={marketplace}
          searchOptions={searchOptionsForQuery}
          showExplainPanel={showExplainPanel}
          language={language}
          callerId={callerId}
          userAgent={userAgent}
          consumerChannelId={consumerChannelId}
          taxonomyIds={taxonomyIds}
          setTaxonomyIds={setTaxonomyIds}
          selectedConcepts={selectedConcepts}
          setSelectedConcepts={setSelectedConcepts}
        />
      )}
    </div>
  );
}
